import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { MfaServices } from "@/services/bedrock/MfaServices";
import { MfaDeliveryMethod } from "@/services/bedrock/dtos";
import { base64ToBuffer, bufferToBase64, formatUsPhone } from "@/utils/mfaHelpers";
import { Smartphone, Mail, Fingerprint, ChevronRight, Loader2, ArrowUp, ArrowDown, CornerDownLeft } from "lucide-react";
import { toast } from "sonner";
import { useSocialCoachSession } from "@/utils";
import { Svg } from "@/assets";
import { useMfaChallengeContext } from "../context";
import { Skeleton } from "@/components/ui";

export const SelectMethod: React.FC = () => {
  const { setStep, setMethod, isLoading, setIsLoading, hasPasskey, setHasPasskey, onSuccess, error, setError } =
    useMfaChallengeContext();

  const { session, isAdmin } = useSocialCoachSession();

  const [focusedIndex, setFocusedIndex] = useState(0);
  const [keyboardReady, setKeyboardReady] = useState(false);

  // Keyboard ready delay to prevent capturing keys pressed in previous dialog contexts
  useEffect(() => {
    const timer = setTimeout(() => setKeyboardReady(true), 150);
    return () => {
      clearTimeout(timer);
      setKeyboardReady(false);
    };
  }, []);

  // Dynamic available methods based on account configurations
  const availableMethods = useMemo(() => {
    const list: {
      id: MfaDeliveryMethod | "passkey";
      title: string;
      subtitle: string;
      icon: React.ComponentType<any>;
      iconColorClass: string;
      iconBgClass: string;
    }[] = [];

    // Passkey option (if supported and registered)
    if (hasPasskey !== false) {
      list.push({
        id: "passkey",
        title: "Passkey",
        subtitle: "Use biometrics or screen lock",
        icon: Fingerprint,
        iconColorClass: "text-indigo-600",
        iconBgClass: "bg-indigo-100 group-hover:bg-indigo-200",
      });
    }

    // SMS option (if configured)
    if (session?.mfaPhone?.verifiedAt) {
      list.push({
        id: "sms",
        title: "Text Message (SMS)",
        subtitle: `Send code to ${formatUsPhone(session.mfaPhone.phoneNumber)}`,
        icon: Smartphone,
        iconColorClass: "text-blue-600",
        iconBgClass: "bg-blue-100 group-hover:bg-blue-200",
      });
    }

    // Email option (always enabled)
    list.push({
      id: "email",
      title: "Email Address",
      subtitle: `Send code to ${session?.email || ""}`,
      icon: Mail,
      iconColorClass: "text-emerald-600",
      iconBgClass: "bg-emerald-100 group-hover:bg-emerald-200",
    });

    // Slack option (Admins only)
    if (isAdmin) {
      list.push({
        id: "slack",
        title: "Slack Direct Message",
        subtitle: "Send secure code to your connected Slack account",
        icon: (props: any) => <img src={Svg.SlackSVG} alt="Slack" className={props.className} />,
        iconColorClass: "",
        iconBgClass: "bg-slate-100 group-hover:bg-slate-200",
      });
    }

    return list;
  }, [session, hasPasskey, isAdmin]);

  // Global keydown listener for the method selection step
  useEffect(() => {
    if (availableMethods.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLoading || !keyboardReady) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % availableMethods.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + availableMethods.length) % availableMethods.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const selected = availableMethods[focusedIndex];
        if (selected) {
          handleSelectMethod(selected.id);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLoading, availableMethods, focusedIndex, keyboardReady]);

  // Check for passkey availability on mount
  useEffect(() => {
    if (hasPasskey === null) {
      MfaServices.getAuthChallenge()
        .then(options => setHasPasskey(!!options.allowCredentials?.length))
        .catch(() => setHasPasskey(false));
    }
  }, [hasPasskey, setHasPasskey]);

  const handleSelectMethod = async (selectedMethod: MfaDeliveryMethod | "passkey") => {
    setError(null);
    setMethod(selectedMethod);
    setIsLoading(true);
    try {
      if (selectedMethod === "passkey") {
        setStep("VERIFYING_PASSKEY");
        const options = await MfaServices.getAuthChallenge();

        const credential = (await navigator.credentials.get({
          publicKey: {
            challenge: base64ToBuffer(options.challenge),
            rpId: options.rpId,
            allowCredentials: options.allowCredentials?.map(c => ({
              ...c,
              id: base64ToBuffer(c.id),
            })),
            userVerification: options.userVerification,
            timeout: options.timeout,
          },
        })) as PublicKeyCredential;

        if (!credential) throw new Error("Passkey authentication failed");

        const response = credential.response as AuthenticatorAssertionResponse;
        const payload = {
          id: credential.id,
          rawId: bufferToBase64(credential.rawId),
          type: credential.type,
          response: {
            authenticatorData: bufferToBase64(response.authenticatorData),
            clientDataJSON: bufferToBase64(response.clientDataJSON),
            signature: bufferToBase64(response.signature),
            userHandle: response.userHandle ? bufferToBase64(response.userHandle) : null,
          },
          origin: window.location.origin,
        };

        const result = await MfaServices.verifyAuth(payload);
        if (result.status === "success") {
          toast.success("Identity verified");
          onSuccess();
        } else {
          setError("Verification failed");
          toast.error("Verification failed");
          setStep("SELECT_METHOD");
        }
      } else {
        await MfaServices.requestChallenge(selectedMethod as MfaDeliveryMethod);
        setStep("ENTER_CODE");
      }
    } catch (e: any) {
      const apiError =
        e.response?.data?.error || e.response?.data?.message || e.message || "Failed to initiate challenge";
      setError(apiError);
      toast.error(apiError);
      console.error(e);
      setStep("SELECT_METHOD");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 relative animate-in fade-in duration-200">
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center gap-3 rounded-xl">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm font-semibold text-slate-600">Sending security code...</span>
        </div>
      )}
      <p className="text-sm text-slate-500 mb-6">
        Choose how you'd like to receive your security code to verify your identity.
      </p>

      {error && (
        <div className="p-3.5 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-xs font-semibold flex items-start gap-2.5 animate-in fade-in duration-200">
          <div className="shrink-0 h-4 w-4 bg-rose-200 rounded-full flex items-center justify-center text-[10px] font-extrabold text-rose-700">
            !
          </div>
          <p className="flex-1 leading-normal">{error}</p>
        </div>
      )}

      {hasPasskey === null ? (
        <div className="grid gap-3">
          {[...Array(2)].map((_, i) => (
            <div
              key={`skeleton-${i}`}
              className="h-16 flex items-center justify-between px-6 border border-slate-100 rounded-xl bg-slate-50/50 animate-pulse"
            >
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
              <Skeleton className="h-4 w-4 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid gap-3">
            {availableMethods.map((m, idx) => {
              const isFocused = idx === focusedIndex;
              return (
                <Button
                  key={m.id}
                  variant="outline"
                  className={`h-16 justify-between px-6 border-slate-200 transition-all group ${
                    isFocused
                      ? "border-primary bg-primary/5 shadow-sm text-primary animate-in fade-in zoom-in-98 duration-150"
                      : "hover:border-slate-300 hover:bg-slate-50/50"
                  }`}
                  onClick={() => handleSelectMethod(m.id)}
                  disabled={isLoading}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors shrink-0 ${
                        m.iconBgClass
                      } ${isFocused ? "bg-white border border-slate-200/60 shadow-sm" : ""}`}
                    >
                      <m.icon className={`h-5 w-5 ${m.iconColorClass}`} />
                    </div>
                    <div className="text-left leading-tight">
                      <div className={`text-sm font-semibold ${isFocused ? "text-primary" : "text-slate-700"}`}>
                        {m.title}
                      </div>
                      <div className={`text-xs ${isFocused ? "text-primary/70" : "text-slate-400"}`}>{m.subtitle}</div>
                    </div>
                  </div>
                  <ChevronRight
                    className={`h-4 w-4 shrink-0 transition-transform ${
                      isFocused ? "text-primary translate-x-1" : "text-slate-300 group-hover:text-primary"
                    }`}
                  />
                </Button>
              );
            })}
          </div>

          {availableMethods.length > 1 && (
            <div className="flex items-center gap-4 text-[10px] text-slate-400 font-semibold select-none pt-4 mt-6 border-t border-slate-100">
              <span className="flex items-center gap-1">
                <ArrowUp className="h-3.5 w-3.5" />
                <ArrowDown className="h-3.5 w-3.5" />
                <span>to navigate</span>
              </span>
              <div className="w-px h-2.5 bg-slate-200" />
              <span className="flex items-center gap-1">
                <CornerDownLeft className="h-3 w-3" />
                <span>to select</span>
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};
